import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';

/**
 * Datum zadnje izmjene Datenschutzerklärung. Ažurirati pri svakoj
 * sadržinskoj izmjeni (DSGVO čl. 13 — informacije o obradi moraju biti
 * "verständlich, transparent" i datirane).
 */
const LAST_UPDATED = 'Mai 2026';

export const DatenschutzScreen: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-accent-pink hover:text-accent-purple mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('datenschutz.back')}
        </Link>

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="prose prose-gray max-w-none"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">
            {t('datenschutz.title')}
          </h1>
          <p className="text-sm text-gray-500 mb-8">{t('datenschutz.subtitle')}</p>

          <section className="space-y-8 text-gray-700">
            {/* 1. Verantwortlicher */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-accent-pink" />
                1. {t('datenschutz.section1Title')}
              </h2>
              <p className="text-sm">{t('datenschutz.section1Desc')}</p>
              <p className="text-sm mt-2 font-medium">
                SUTON Media GmbH<br />
                Zeilweg 17-19<br />
                60439 Frankfurt am Main
              </p>
              <p className="text-sm mt-2">
                E-Mail:{' '}
                <a href="mailto:info@suton-radio.de" className="text-accent-pink hover:underline">
                  info@suton-radio.de
                </a>
              </p>
            </div>

            {/* 2. Erhobene Daten */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                2. {t('datenschutz.section2Title')}
              </h2>
              <p className="text-sm mb-2">{t('datenschutz.section2Desc')}</p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>{t('datenschutz.section2LocalStorage')}</li>
                <li>{t('datenschutz.section2NoLogin')}</li>
                <li>{t('datenschutz.section2StreamMeta')}</li>
              </ul>
            </div>

            {/* 3. Eingesetzte Dienste und Drittanbieter */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                3. {t('datenschutz.section3Title')}
              </h2>
              <p className="text-sm mb-3">{t('datenschutz.section3Desc')}</p>

              <h3 className="text-base font-semibold text-gray-800 mb-2">
                {t('datenschutz.section3OwnTitle')}
              </h3>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2 mb-4">
                <li>{t('datenschutz.section3OwnCms')}</li>
                <li>{t('datenschutz.section3OwnStream')}</li>
                <li>{t('datenschutz.section3OwnVideo')}</li>
                <li>{t('datenschutz.section3OwnTranslate')}</li>
              </ul>

              <h3 className="text-base font-semibold text-gray-800 mb-2">
                {t('datenschutz.section3ThirdTitle')}
              </h3>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>{t('datenschutz.section3ThirdGa')}</li>
                <li>{t('datenschutz.section3ThirdFonts')}</li>
                <li>{t('datenschutz.section3ThirdMaps')}</li>
                <li>{t('datenschutz.section3ThirdZeno')}</li>
                <li>{t('datenschutz.section3ThirdStations')}</li>
              </ul>
            </div>

            {/* 4. Cookies und lokale Speicherung */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                4. {t('datenschutz.section4Title')}
              </h2>
              <p className="text-sm mb-2">{t('datenschutz.section4Desc')}</p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>{t('datenschutz.section4Necessary')}</li>
                <li>{t('datenschutz.section4Analytics')}</li>
                <li>{t('datenschutz.section4NoAds')}</li>
              </ul>
            </div>

            {/* 5. Speicherdauer */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                5. {t('datenschutz.section5Title')}
              </h2>
              <p className="text-sm">{t('datenschutz.section5Desc')}</p>
            </div>

            {/* 6. Berechtigungen der App */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                6. {t('datenschutz.section6Title')}
              </h2>
              <p className="text-sm mb-2">{t('datenschutz.section6Desc')}</p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>{t('datenschutz.section6Internet')}</li>
                <li>{t('datenschutz.section6Network')}</li>
                <li>{t('datenschutz.section6Wake')}</li>
                <li>{t('datenschutz.section6Foreground')}</li>
                <li>{t('datenschutz.section6Notifications')}</li>
                <li>{t('datenschutz.section6Audio')}</li>
              </ul>
            </div>

            {/* 7. Ihre Rechte */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                7. {t('datenschutz.section7Title')}
              </h2>
              <p className="text-sm mb-2">{t('datenschutz.section7Desc')}</p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>{t('datenschutz.section7Right1')}</li>
                <li>{t('datenschutz.section7Right2')}</li>
                <li>{t('datenschutz.section7Right3')}</li>
                <li>{t('datenschutz.section7Right4')}</li>
                <li>{t('datenschutz.section7Right5')}</li>
                <li>{t('datenschutz.section7Right6')}</li>
                <li>{t('datenschutz.section7Right7')}</li>
                <li>{t('datenschutz.section7Right8')}</li>
              </ul>
              <p className="text-sm mt-2">
                {t('datenschutz.section7Contact')}:{' '}
                <a href="mailto:info@suton-radio.de" className="text-accent-pink hover:underline">
                  info@suton-radio.de
                </a>
              </p>
            </div>

            {/* 8. Datensicherheit */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                8. {t('datenschutz.section8Title')}
              </h2>
              <p className="text-sm">{t('datenschutz.section8Desc')}</p>
            </div>

            {/* 9. Externe Links */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                9. {t('datenschutz.section9Title')}
              </h2>
              <p className="text-sm">{t('datenschutz.section9Desc')}</p>
            </div>

            {/* 10. Änderungen dieser Datenschutzerklärung */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                10. {t('datenschutz.section10Title')}
              </h2>
              <p className="text-sm">{t('datenschutz.section10Desc')}</p>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                {t('datenschutz.lastUpdate')}: {LAST_UPDATED}
              </p>
            </div>
          </section>
        </motion.article>

        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-accent-pink hover:text-accent-purple transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('datenschutz.back')}
          </Link>
        </div>
      </div>
    </div>
  );
};
